import { useMemo, useRef, useState } from "react";

import { Box, Divider, SvgIcon } from "@mui/material";
import { StriimTooltip, StriimTypography } from "@striim/striim-ui";

import HelpIcon from "../../../../../../src/generic/icon/topbar/help.svg";
import { Styles, StylesTheme } from "../../../../../../src/utils/types";
import { LeftSidebarCategory, LeftSidebarSubcategory } from "../leftSidebar.types";
import { SearchBar } from "../../../../../../src/modules/ai-insights/guardian/discovery/components/jobs-list-table";
import ExpandSubcategory from "./ExpandSubcategory";
import ComponentDnD from "./ComponentDnD";
import { allSubcategoryName } from "../../predefinedComponents/predefinedComponentsDataSource";
import { getCustomDisableReason } from "./CategoryContent.utils";
import { Close } from "@mui/icons-material";
import { StriimButton } from "@striim/striim-ui-v2";

interface CategoryContentProps {
    isSearchCategory?: boolean;
    data: LeftSidebarCategory[];
    categoryIndex: number;
    onClose: () => void;
}

const CategoryContent = ({ isSearchCategory, data, categoryIndex, onClose }: CategoryContentProps) => {
    const scrollableRef = useRef<HTMLDivElement>(undefined);

    const [searchInput, setSearchInput] = useState("");

    const currentSubcategories = useMemo<LeftSidebarSubcategory[]>(() => {
        if (isSearchCategory) {
            if (!searchInput) return [];

            const simplySearchInput = searchInput.toLowerCase();

            return data.reduce<LeftSidebarSubcategory[]>((acc, curr) => {
                acc.push(
                    ...curr.subcategories
                        .filter(subcategory => subcategory.name === allSubcategoryName)
                        .map(subcategory => ({
                            ...subcategory,
                            components: subcategory.components.filter(component =>
                                component.name.toLowerCase().includes(simplySearchInput)
                            ),
                            name: curr.categoryLabel
                        }))
                        .filter(subcategory => subcategory.components.length > 0)
                );
                return acc;
            }, []);
        }

        if (!searchInput) return data[categoryIndex].subcategories;

        const simplySearchInput = searchInput.toLowerCase();

        return data[categoryIndex].subcategories
            .filter(subcategory => subcategory.name === allSubcategoryName)
            .map(subcategory => ({
                ...subcategory,
                components: subcategory.components.filter(component =>
                    component.name.toLowerCase().includes(simplySearchInput)
                )
            }))
            .filter(subcategory => subcategory.components.length > 0);
    }, [searchInput, isSearchCategory, data, categoryIndex]);

    return (
        <Box sx={styles.root} data-testid="category-content">
            <Box sx={styles.titleWrap}>
                <StriimTypography variant="h4" sx={styles.title} color="primary.700" data-testid="category-title">
                    {isSearchCategory ? "Search" : data[categoryIndex].categoryLabel}
                    {!isSearchCategory && data[categoryIndex].categoryTooltip ? (
                        <StriimTooltip title={data[categoryIndex].categoryTooltip} placement="right">
                            <Box sx={styles.tooltipIconWrap}>
                                <SvgIcon component={HelpIcon} sx={styles.tooltipIcon} />
                            </Box>
                        </StriimTooltip>
                    ) : null}
                </StriimTypography>
                <StriimButton
                    variant="primaryText"
                    startIcon={<Close />}
                    sx={styles.closeButton}
                    onClick={onClose}
                    data-testid="close-subcategory"
                />
            </Box>
            <Divider />

            <Box sx={styles.searchFilterWrap}>
                <SearchBar
                    inputDataTestId="search-adapter-input"
                    isGroupTab={false}
                    setUserSearchInput={setSearchInput}
                    customPlaceholder={
                        "Search" +
                        (!isSearchCategory && data[categoryIndex].categorySearchSuffix
                            ? " " + data[categoryIndex].categorySearchSuffix
                            : "")
                    }
                />
            </Box>
            <Divider />

            <Box
                sx={styles.scrollableWrap}
                key={
                    /* Fix lazy render when: search (e.g. just "a"), sroll to middle, remove/change search, scroll */
                    searchInput
                }
            >
                <Box data-testid="category-subcategories" sx={styles.scrollable} ref={scrollableRef}>
                    {isSearchCategory ? (
                        currentSubcategories.length === 0 ? (
                            <Box sx={styles.noSearchResults}>
                                {searchInput ? (
                                    <Box>
                                        <StriimTypography variant="body3" color="primary.900">
                                            No components matching{" "}
                                            <Box component={"span"} sx={{ wordBreak: "break-word" }}>
                                                “{searchInput}”
                                            </Box>
                                        </StriimTypography>
                                        <br />
                                        <StriimTypography variant="body4" color="greyscale.700">
                                            Try checking for typos or search using another keyword
                                        </StriimTypography>
                                    </Box>
                                ) : (
                                    <StriimTypography variant="body4" color="greyscale.700">
                                        Search across all components and existing resources
                                    </StriimTypography>
                                )}
                            </Box>
                        ) : (
                            currentSubcategories.map((subcategory, i) => (
                                <ExpandSubcategory
                                    key={subcategory.name}
                                    subcategory={subcategory}
                                    scrollableRef={scrollableRef}
                                    initialInView={i === 0}
                                />
                            ))
                        )
                    ) : currentSubcategories.length === 0 ? (
                        <Box sx={styles.noSearchResults}>No results found</Box>
                    ) : currentSubcategories.length === 1 ? (
                        currentSubcategories[0].components.map((component, i) => (
                            <ComponentDnD
                                key={searchInput}
                                id={component.id}
                                name={component.name}
                                disabled={component.isEnabled === false}
                                customDisableReason={getCustomDisableReason(component)}
                                type={component.type}
                                scrollableRef={scrollableRef}
                                initialInView={i < 15}
                            />
                        ))
                    ) : (
                        currentSubcategories.map((subcategory, i) => (
                            <ExpandSubcategory
                                key={subcategory.name}
                                subcategory={subcategory}
                                scrollableRef={scrollableRef}
                                initialInView={i === 0}
                            />
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default CategoryContent;

const styles: Styles = {
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "256px",
        backgroundColor: ({ palette }: StylesTheme) => palette.background.paper,
        borderRight: ({ palette }: StylesTheme) => `1px solid ${palette.divider}`
    },
    titleWrap: {
        display: "flex",
        justifyContent: "space-between",
        padding: ({ spacing }: StylesTheme) => spacing(0.5, 0)
    },
    title: {
        display: "flex",
        padding: [2, 1.5],
        alignSelf: "stretch",
        alignItems: "center"
    },
    tooltipIconWrap: {
        display: "flex"
    },
    tooltipIcon: {
        marginLeft: 0.5,
        height: 16,
        width: 16,
        fill: "transparent",
        "& path": {
            stroke: theme => theme.palette.greyscale[600]
        }
    },
    closeButton: {
        margin: ({ spacing }: StylesTheme) => spacing("auto", 0.5, "auto", 0),
        minWidth: "initial",
        padding: ({ spacing }: StylesTheme) => spacing(1),

        "& .MuiButton-startIcon": {
            mx: 0
        }
    },
    searchFilterWrap: {
        padding: ({ spacing }: StylesTheme) => spacing(1.5, 1)
    },
    scrollableWrap: {
        display: "contents"
    },
    scrollable: {
        overflowY: "auto",
        display: "block"
    },
    noSearchResults: {
        display: "flex",
        justifyContent: "center",
        pt: 3,
        px: 3,
        textAlign: "center"
    }
};
